
import React, { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import {
  inventoryCategoryOptions,
  paymentTypeOptions,
  paymentCadenceOptions,
  InventoryItem,
  InventoryCategory,
} from "./inventoryTypes";
import { fetchCompanyEmployees, fetchStorageLocations, useInventoryOperations } from "./inventoryService";

const formSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  serial: z.string().optional(),
  description: z.string().optional(),
  category: z.string().min(1, { message: "Category is required" }),
  cost: z.string().optional(),
  purchase_date: z.date().optional().nullable(),
  authorized_by: z.string().optional(),
  payment_type: z.string().min(1, { message: "Payment type is required" }),
  payment_cadence: z.string().optional(),
  next_payment_date: z.date().optional().nullable(),
  payment_amount: z.string().optional(),
  location: z.string().min(1, { message: "Location is required" }),
  isNewPurchase: z.boolean().default(false),
});

interface NewInventoryItemDialogProps {
  open: boolean;
  onClose: (created: boolean) => void;
}

export function NewInventoryItemDialog({ open, onClose }: NewInventoryItemDialogProps) {
  const { handleInventoryItemCreate } = useInventoryOperations();
  
  const { data: employees = [], isLoading: isEmployeesLoading } = useQuery({
    queryKey: ["companyEmployees"],
    queryFn: fetchCompanyEmployees,
  });
  
  const { data: locations = [], isLoading: isLocationsLoading } = useQuery({
    queryKey: ["storageLocations"],
    queryFn: fetchStorageLocations,
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      serial: "",
      description: "",
      category: "Hardware",
      cost: "",
      purchase_date: null,
      authorized_by: "",
      payment_type: "One Time",
      payment_cadence: undefined,
      next_payment_date: null,
      payment_amount: "",
      location: locations && locations.length > 0 ? locations[0]?.id : "",
      isNewPurchase: false,
    },
  });

  // Update the location field when locations are loaded
  React.useEffect(() => {
    if (locations && locations.length > 0 && !form.getValues().location) {
      form.setValue("location", locations[0].id);
    }
  }, [locations, form]);

  const watchPaymentType = form.watch("payment_type");
  const watchIsNewPurchase = form.watch("isNewPurchase");
  const watchCategory = form.watch("category");

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    // Create the base item with required fields
    const newItem: Partial<InventoryItem> & { category: InventoryCategory; name: string } = {
      name: data.name,
      category: data.category as InventoryCategory,
      status: "Available",
      location: data.location,
      payment_type: data.payment_type as any,
    };
    
    // Add serial number if it's hardware or provided
    if (data.category === "Hardware" && data.serial) {
      newItem.serial = data.serial;
    }
    
    // Add optional fields if provided
    if (data.description) {
      newItem.description = data.description;
    }
    
    // Add cost if provided
    if (data.cost) {
      newItem.cost = parseFloat(data.cost);
      newItem.currency = "USD"; // Default currency
    }
    
    // Add purchase date if provided
    if (data.purchase_date) {
      newItem.purchase_date = data.purchase_date.toISOString().split("T")[0];
    }
    
    // Add authorizer if it's a new purchase
    if (data.isNewPurchase && data.authorized_by) {
      newItem.authorized_by = parseInt(data.authorized_by);
    }
    
    // Add recursive payment details if applicable
    if (data.payment_type === "Recursive") {
      newItem.payment_cadence = data.payment_cadence as any;
      
      if (data.payment_amount) {
        newItem.payment_amount = parseFloat(data.payment_amount);
      }
      
      if (data.next_payment_date) {
        newItem.next_payment_date = data.next_payment_date.toISOString().split("T")[0];
      }
    }
    
    const success = await handleInventoryItemCreate(newItem);
    onClose(success);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose(false)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New Inventory Item</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Item Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {inventoryCategoryOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {watchCategory === "Hardware" && (
                <FormField
                  control={form.control}
                  name="serial"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Serial Number</FormLabel>
                      <FormControl>
                        <Input placeholder="SN123456789" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className={watchCategory === "Hardware" ? "" : "col-span-2"}>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter item description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="payment_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select payment type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {paymentTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                    {watchPaymentType === "Recursive" && (
                      <FormDescription>
                        For recurring payments like subscriptions or rentals
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              
              {watchPaymentType === "Recursive" && (
                <FormField
                  control={form.control}
                  name="payment_cadence"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Cadence</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select payment cadence" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {paymentCadenceOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              
              <FormField
                control={form.control}
                name="cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {watchPaymentType === "Recursive" ? "Initial Cost (USD)" : "Cost (USD)"}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="0.00"
                        step="0.01"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    {watchPaymentType === "Recursive" && (
                      <FormDescription>
                        One-time purchase or setup cost
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              
              {watchPaymentType === "Recursive" && (
                <FormField
                  control={form.control}
                  name="payment_amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Recurring Amount (USD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="0.00"
                          step="0.01"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                      <FormDescription>
                        Amount to be paid each billing period
                      </FormDescription>
                    </FormItem>
                  )}
                />
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="purchase_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purchase Date</FormLabel>
                    <FormControl>
                      <DatePicker
                        date={field.value}
                        setDate={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {watchPaymentType === "Recursive" && (
                <FormField
                  control={form.control}
                  name="next_payment_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Next Payment Date</FormLabel>
                      <FormControl>
                        <DatePicker
                          date={field.value}
                          setDate={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
            
            <FormField
              control={form.control}
              name="isNewPurchase"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2 rounded-lg border p-4">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div>
                    <FormLabel>New Purchase</FormLabel>
                    <FormDescription>
                      Is this a new purchase that needs authorization?
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
            
            {watchIsNewPurchase && (
              <FormField
                control={form.control}
                name="authorized_by"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Authorized By</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an authorizer" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {isEmployeesLoading ? (
                          <SelectItem value="loading">Loading...</SelectItem>
                        ) : employees.length === 0 ? (
                          <SelectItem value="none">No employees found</SelectItem>
                        ) : (
                          employees.map((employee) => (
                            <SelectItem key={employee.id} value={employee.id.toString()}>
                              {employee.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                    <FormDescription>
                      Only employees from your company are shown
                    </FormDescription>
                  </FormItem>
                )}
              />
            )}
            
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Storage Location</FormLabel>
                  {locations && locations.length > 0 ? (
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value || (locations[0]?.id || "")}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a location" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {locations.map((location) => (
                          <SelectItem key={location.id} value={location.id}>
                            {location.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex flex-col space-y-2">
                      <p className="text-sm text-muted-foreground">No storage locations available.</p>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        asChild
                      >
                        <Link to="/inventory?tab=locations" className="flex items-center">
                          Create a storage location 
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onClose(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isLocationsLoading || (locations && locations.length === 0)}
              >
                Add Item
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
